import type { PortLike } from "./types";

/** A PortLike proxy that allows the underlying port to be hot-swapped while preserving listeners. */
export class SwappablePort implements PortLike {
  private _active_port: PortLike | null = null;
  private _listeners: TrackedListener[] = [];

  replacePort(port: PortLike): void {
    const oldPort = this._active_port;

    // Remove all tracked listeners from the old port
    if (oldPort) {
      for (const entry of this._listeners) {
        oldPort.removeEventListener(entry.type, entry.listener, entry.options);
      }
      oldPort.close?.();
    }

    this._active_port = port;

    // Re-attach all tracked listeners to the new port
    for (const entry of this._listeners) {
      port.addEventListener(entry.type, entry.listener, entry.options);
    }

    port.start?.();
  }

  // biome-ignore lint/suspicious/noExplicitAny: Because
  postMessage(message: any, transfer?: Transferable[]): void {
    if (!this._active_port) {
      throw new Error("No active port");
    }
    this._active_port.postMessage(message, transfer);
  }

  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | AddEventListenerOptions,
  ): void {
    this._listeners.push({ type, listener, options });
    this._active_port?.addEventListener(type, listener, options);
  }

  removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | EventListenerOptions,
  ): void {
    const capture = captureOf(options);
    const idx = this._listeners.findIndex(
      (e) => e.type === type && e.listener === listener && captureOf(e.options) === capture,
    );
    if (idx !== -1) {
      this._listeners.splice(idx, 1);
    }
    this._active_port?.removeEventListener(type, listener, options);
  }

  dispatchEvent(event: Event): boolean {
    if (!this._active_port) {
      return false;
    }
    return this._active_port.dispatchEvent(event);
  }

  start(): void {
    this._active_port?.start?.();
  }

  close(): void {
    this._active_port?.close?.();
  }
}

interface TrackedListener {
  type: string;
  listener: EventListenerOrEventListenerObject | null;
  options?: boolean | AddEventListenerOptions;
}

function captureOf(opts?: boolean | AddEventListenerOptions | EventListenerOptions): boolean {
  if (typeof opts === "boolean") return opts;
  return opts?.capture ?? false;
}
